import { useState, useCallback, useRef } from 'react';
import { useAuth } from '../state/AuthContext';
import { useNewReleases } from '../hooks/useNewReleases';
import { useAudioPreview } from '../hooks/useAudioPreview';
import { getAlbumTracks, getArtistTopTracks } from '../api/deezerClient';
import { buildAlbumSearchUrl, DEFAULT_MUSIC_SERVICE } from '../utils/exportUtils';
import '../styles/new-releases.css';

function formatRelativeDate(dateStr) {
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const releaseMs = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const diff = Date.now() - releaseMs;
  const days = Math.round(diff / (1000 * 60 * 60 * 24));
  if (days < 0) {
    const abs = Math.abs(days);
    if (abs <= 1) return 'Tomorrow';
    if (abs < 7) return `in ${abs}d`;
    if (abs < 30) return `in ${Math.floor(abs / 7)}w`;
    return `in ${Math.floor(abs / 30)}mo`;
  }
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

// SVG circle constants for the progress ring
const RING_RADIUS = 13;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function NewReleasesSection({ showHeader }) {
  const { user } = useAuth();
  const { releases, isLoading } = useNewReleases();
  const [loadingAlbumId, setLoadingAlbumId] = useState(null);
  const [playingAlbumId, setPlayingAlbumId] = useState(null);
  const playingAlbumIdRef = useRef(null);

  const onEnded = useCallback(() => {
    setPlayingAlbumId(null);
    playingAlbumIdRef.current = null;
  }, []);

  const { isPlaying, progress, play, pause } = useAudioPreview({
    onEnded,
  });

  const handlePlayClick = useCallback(async (e, album) => {
    e.preventDefault();
    e.stopPropagation();

    // Toggle off if already playing this album
    if (playingAlbumIdRef.current === album.id && isPlaying) {
      pause();
      setPlayingAlbumId(null);
      playingAlbumIdRef.current = null;
      return;
    }

    setLoadingAlbumId(album.id);

    try {
      // Try to get the first track of the album
      let tracks = await getAlbumTracks(album.id, 1);

      // Fallback: get top track for the artist
      if ((!tracks.length || !tracks[0].previewUrl) && album.artistId) {
        tracks = await getArtistTopTracks(album.artistId, 1, { priority: true });
      }

      if (tracks.length && tracks[0].previewUrl) {
        play(tracks[0]);
        setPlayingAlbumId(album.id);
        playingAlbumIdRef.current = album.id;
      }
    } catch (err) {
      console.warn('Failed to load preview for release:', err.message);
    } finally {
      setLoadingAlbumId(null);
    }
  }, [isPlaying, play, pause]);

  if (!user) return null;
  if (!isLoading && releases.length === 0) return null;

  return (
    <div className="new-releases-section">
      {showHeader && (
        <h3 className="tab-section-header" style={{ marginBottom: '12px' }}>
          New Releases
          {!isLoading && releases.length > 0 && <span className="section-count">{releases.length}</span>}
        </h3>
      )}
      {isLoading && (
        <div className="new-releases-computing">
          <div className="new-releases-computing-spinner" />
          <span className="new-releases-computing-text">Loading your New Releases...</span>
        </div>
      )}

      {!isLoading && releases.length > 0 && (
        <div className="new-releases-scroll">
          {releases.map((album, index) => {
            const isThisPlaying = playingAlbumId === album.id && isPlaying;
            const isThisLoading = loadingAlbumId === album.id;
            const showProgress = isThisPlaying && progress > 0;
            const strokeDashoffset = showProgress
              ? RING_CIRCUMFERENCE * (1 - progress)
              : RING_CIRCUMFERENCE;

            return (
              <a
                key={album.id}
                className="new-release-card"
                href={buildAlbumSearchUrl(album.artistName, album.title, user?.musicService || DEFAULT_MUSIC_SERVICE)}
                target="_blank"
                rel="noopener noreferrer"
                title={`${album.title} by ${album.artistName}`}
              >
                {album.coverMedium ? (
                  <img className="new-release-cover" src={album.coverMedium} alt={album.title} loading={index < 6 ? "eager" : "lazy"} />
                ) : (
                  <div className="new-release-cover new-release-cover-placeholder">
                    {album.title.charAt(0)}
                  </div>
                )}
                <div className="new-release-info-row">
                  <div className="new-release-info-text">
                    <span className="new-release-album-name">{album.title}</span>
                    <span className="new-release-artist-name">{album.artistName}</span>
                    <span className="new-release-date">{formatRelativeDate(album.releaseDate)}</span>
                  </div>
                  <button
                    className={`new-release-play-btn${isThisPlaying ? ' playing' : ''}${isThisLoading ? ' loading' : ''}`}
                    onClick={(e) => handlePlayClick(e, album)}
                    aria-label={isThisPlaying ? 'Pause preview' : 'Play preview'}
                  >
                    <svg className="new-release-play-ring" viewBox="0 0 32 32">
                      <circle
                        className="new-release-play-ring-bg"
                        cx="16" cy="16" r={RING_RADIUS}
                      />
                      <circle
                        className="new-release-play-ring-progress"
                        cx="16" cy="16" r={RING_RADIUS}
                        strokeDasharray={RING_CIRCUMFERENCE}
                        strokeDashoffset={strokeDashoffset}
                      />
                    </svg>
                    {isThisLoading ? (
                      <span className="new-release-play-spinner" />
                    ) : isThisPlaying ? (
                      <svg className="new-release-play-icon" viewBox="0 0 16 16" fill="currentColor">
                        <rect x="4" y="3" width="3" height="10" rx="0.5" />
                        <rect x="9" y="3" width="3" height="10" rx="0.5" />
                      </svg>
                    ) : (
                      <svg className="new-release-play-icon" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M5.5 3.5v9l7-4.5z" />
                      </svg>
                    )}
                  </button>
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default NewReleasesSection;
